import { CaseListDto, SortDirection, SortingDto } from '@lmt/shared-types';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';

const dateFormat = 'P';
const dateLongFormat = 'PP';
const dateTimeLongFormat = 'PPp';
const dateTimeFormat = 'Pp';
const timeFormat = 'p';
const monthFormat = 'LLL';
const longMonthFormat = 'LLLL';
const dateLongMonthFormat = 'PPP';

export function getFormattedLongMonthAndYear(date: Date): string {
  const month = localFormat(date, longMonthFormat);
  return `${month} ${date.getFullYear()}`;
}

export function getFormattedDateRange(start: Date, end?: Date | null): string {
  const startDate = localFormat(start, dateFormat);
  const endDate = end && `-${localFormat(end, dateFormat)}`;
  return [startDate, endDate].join('');
}

export function getFormattedLongDateRange(
  start?: Date | null,
  end?: Date | null,
): string {
  if (!start) {
    return '';
  }
  if (!end) {
    return getFormattedLongDate(start);
  }
  if (start.getUTCFullYear() === end.getUTCFullYear()) {
    return (
      getLongDateWithoutYearRange(start, end) + ' ' + start.getUTCFullYear()
    );
  } else {
    return getFormattedLongDate(start) + ' - ' + getFormattedLongDate(end);
  }
}

export function getFormattedDate(date: Date | undefined | null): string {
  return date ? localFormat(date, dateFormat) : '';
}

export function getFormattedDateTime(date?: Date | null): string {
  return date ? localFormat(date, dateTimeFormat) : '';
}

export function getFormattedTime(date: Date) {
  return localFormat(date, timeFormat);
}

export function getFormattedLongDate(date: Date): string {
  return localFormat(date, dateLongFormat);
}

export function getFormattedLongDateTime(date: Date): string {
  return localFormat(date, dateTimeLongFormat);
}

export function getLongMonthDateWithoutYear(date: Date): string {
  const month = localFormat(date, longMonthFormat);
  return `${month}, ${date.getDate()}`;
}

export function getLongMonthDate(date: Date): string {
  return localFormat(date, dateLongMonthFormat);
}

function getDateWithoutYearForRange(date: Date): string {
  const month = localFormat(date, monthFormat);
  return `${date.getDate()} ${month}`;
}

export function getLongDateWithoutYearRange(startDate: Date, endDate: Date) {
  if (startDate.getDate() === endDate.getDate()) {
    return getDateWithoutYearForRange(startDate);
  }
  const startMonth = localFormat(startDate, monthFormat);
  if (startDate.getMonth() === endDate.getMonth()) {
    return `${startDate.getDate()}-${endDate.getDate()} ${startMonth}`;
  }
  return `${getDateWithoutYearForRange(
    startDate,
  )} - ${getDateWithoutYearForRange(endDate)}`;
}

export function getFormattedTimeRange(startDate: Date, endDate: Date): string {
  return `${getFormattedTime(startDate)} - ${getFormattedTime(endDate)}`;
}

export function getDateWithoutYear(date: Date): string {
  // At the time of writing there is no format pattern to return day and month without a year in date-fns
  // https://github.com/date-fns/date-fns/issues/1946
  const dateWithYear = localFormat(date, 'PP');
  return removeYearFromDate(date, dateWithYear);
}

export function getDateTimeWithoutYear(date: Date): string {
  // At the time of writing there is no format pattern to return day and month without a year in date-fns
  // https://github.com/date-fns/date-fns/issues/1946
  const dateWithYear = localFormat(date, 'PPp');
  return removeYearFromDate(date, dateWithYear);
}

function removeYearFromDate(date: Date, dateWithYearAsString: string) {
  // At the time of writing there is no format pattern to return day and month without a year in date-fns
  // https://github.com/date-fns/date-fns/issues/1946
  // So, you could format date WITH year, and use this function to remove the year part
  const yearString = date.getFullYear().toString();

  // remove the year and all spaces/dots/commas around it
  return dateWithYearAsString.replace(
    new RegExp(`[\\s\\.,]*${yearString}[\\s\\.,]*`),
    ' ',
  );
}

function getFormattedDateTimeRangeWithoutYear(
  startDate: Date,
  endDate: Date,
): string {
  return `${getDateWithoutYearForRange(startDate)} ${getFormattedTime(startDate)} -
   ${getDateWithoutYearForRange(endDate)} ${getFormattedTime(endDate)}`;
}

export function getFormattedDateTimeRange(
  startDate: Date,
  endDate?: Date,
): string {
  if (!endDate) {
    return getFormattedLongDateTime(startDate);
  }
  if (startDate.getDate() === endDate.getDate()) {
    return `${getFormattedLongDate(startDate)} ${getFormattedTimeRange(
      startDate,
      endDate,
    )}`;
  } else {
    return `${getFormattedDateTimeRangeWithoutYear(
      startDate,
      endDate,
    )}, ${startDate.getFullYear()}`;
  }
}

export function getFormattedDateTimeWithoutYearRange(
  startDate: Date,
  endDate?: Date,
): string {
  if (!endDate) {
    return getFormattedLongDateTime(startDate);
  }
  if (startDate.getDate() === endDate.getDate()) {
    return `${getDateWithoutYearForRange(startDate)} ${getFormattedTimeRange(
      startDate,
      endDate,
    )}`;
  } else {
    return getFormattedDateTimeRangeWithoutYear(startDate, endDate);
  }
}

export enum MiniscreenMode {
  Online = 1,
  Offline = 2,
}

export type MiniscreenObject = {
  patientId?: number;
  measurementId?: number;
  mode?: MiniscreenMode;
};

let _openWithMiniscreen: (object: MiniscreenObject) => Promise<void>;

export function setOpenWithMiniscreen(
  func: (object: MiniscreenObject) => Promise<void>,
) {
  _openWithMiniscreen = func;
}

export function isMiniscreen() {
  return !!_openWithMiniscreen;
}

export async function openWithMiniscreen(object: MiniscreenObject) {
  return _openWithMiniscreen?.(object);
}

let _startMiniscreenOnline = false;
export function setStartMiniscreenOnlineMode(value: boolean) {
  _startMiniscreenOnline = value;
}
export function isStartMiniscreenOnlineModeEnabled() {
  return _startMiniscreenOnline;
}

let _startMiniscreenOffline = false;
export function setStartMiniscreenOfflineMode(value: boolean) {
  _startMiniscreenOffline = value;
}
export function isStartMiniscreenOfflineModeEnabled() {
  return _startMiniscreenOffline;
}

export const minBirthDate = new Date(1900, 0, 1);

export const getLatestCase = (cases: CaseListDto[]): CaseListDto => {
  return cases.reduce((a, b) => (a.startDate > b.startDate ? a : b));
};

export const reasonsForPatientRemoving: string[] = [
  'private_request',
  'insurance_request',
  'government_request',
];

export function getAdditionalSorting(
  sortBy?: string | null,
  desc?: boolean,
): SortingDto[] {
  return sortBy === 'lastName'
    ? [
        {
          sortField: 'firstName',
          sortDirection: desc ? SortDirection.Desc : SortDirection.Asc,
        },
      ]
    : [];
}
